/**
 * PublishReleaseModal Component
 * 
 * Modal for publishing draft changes as a named release
 * Shows summary of changes and allows user to provide release name and description
 */

import { useState } from 'react'
import { Card, Button, Input, Label } from './ui'
import useDraftStore from '../stores/draftStore'
import api from '../services/api'
import {
  resourceMeta,
  resourceDisplayName,
  OPERATION_LABEL,
  PRICING_RESOURCE_TYPES,
} from '../utils/resourceLabels'

export default function PublishReleaseModal({ isOpen, onClose, onSuccess, clinicId }) {
  const [releaseName, setReleaseName] = useState('')
  const [description, setDescription] = useState('')
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState(null)
  
  const { 
    getChangeSummary, 
    getChangesForPublish, 
    clearDrafts,
    changes 
  } = useDraftStore()
  
  const summary = getChangeSummary()

  // Pricing changes are called out separately because they are the ones that
  // move money the moment the release lands.
  const pricingChangeCount = PRICING_RESOURCE_TYPES.reduce(
    (total, type) => total + (summary.by_type[type] || 0),
    0
  )

  const handlePublish = async () => {
    if (!releaseName.trim()) {
      setError('Release name is required')
      return
    }
    
    try {
      setPublishing(true)
      setError(null)
      
      const changesToPublish = getChangesForPublish()
      
      console.log('[PUBLISH] Publishing release:', { releaseName, changeCount: changesToPublish.length })
      
      await api.post(`/${clinicId}/releases/publish`, {
        release_name: releaseName,
        description: description,
        changes: changesToPublish
      })
      
      console.log('[PUBLISH] Release published successfully')
      
      // Clear drafts after successful publish
      clearDrafts()
      
      // Notify parent
      if (onSuccess) {
        onSuccess()
      }
      
      // Close modal
      onClose()
    } catch (err) {
      console.error('[PUBLISH] Error publishing release:', err)
      console.error('[PUBLISH] Error response:', err.response?.data)
      setError(err.response?.data?.message || 'Failed to publish release')
    } finally {
      setPublishing(false)
    }
  }
  
  const getChangeDescription = (change) => {
    const { label, color } = resourceMeta(change.resource_type)
    const name = resourceDisplayName(change)
    const opText = OPERATION_LABEL[change.operation] || change.operation

    return {
      text: `${opText}: ${label} "${name}"`,
      section: label,
      sectionColor: color
    }
  }
  
  if (!isOpen) return null
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="max-w-3xl w-full m-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4">Publish Release</h2>
          
          {/* Release name and description */}
          <div className="space-y-4 mb-6">
            <div>
              <Label htmlFor="release-name">
                Release Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="release-name"
                placeholder="e.g., Q4 2025 Program Updates"
                value={releaseName}
                onChange={(e) => setReleaseName(e.target.value)}
                className="mt-1"
                disabled={publishing}
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <textarea
                id="description"
                placeholder="Describe the changes in this release..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 w-full min-h-[100px] px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                disabled={publishing}
              />
            </div>
          </div>
          
          {/* Changes summary */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Changes to Publish</h3>
            
            <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-muted rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {summary.creates}
                </div>
                <div className="text-sm text-muted-foreground">Created</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {summary.updates}
                </div>
                <div className="text-sm text-muted-foreground">Updated</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {summary.deletes}
                </div>
                <div className="text-sm text-muted-foreground">Deleted</div>
              </div>
            </div>

            {/* By resource type */}
            <div className="flex flex-wrap gap-4 mb-4 text-sm">
              {Object.entries(summary.by_type)
                .filter(([, count]) => count > 0)
                .map(([type, count]) => (
                  <div key={type} className="flex items-center gap-1">
                    <span className="font-medium">{count}</span>
                    <span className="text-muted-foreground">{resourceMeta(type).plural}</span>
                  </div>
                ))}
            </div>

            {/* Detailed change list */}
            <div className="max-h-[300px] overflow-y-auto border rounded-lg">
              <ul className="divide-y">
                {changes.map((change) => {
                  const desc = getChangeDescription(change)
                  return (
                    <li key={change.id} className="p-3 hover:bg-muted/50">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${desc.sectionColor}`}>
                              {desc.section}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              change.operation === 'create' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100' :
                              change.operation === 'update' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100' :
                              'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100'
                            }`}>
                              {change.operation}
                            </span>
                          </div>
                          <div className="font-medium">{desc.text}</div>
                          {change.description && (
                            <div className="text-sm text-muted-foreground mt-1">
                              {change.description}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground mt-1">
                            {new Date(change.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
          
          {/* Warning */}
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-start gap-2">
              <span className="text-amber-600 dark:text-amber-400 text-xl">⚠️</span>
              <div className="text-sm">
                <div className="font-medium text-amber-900 dark:text-amber-100 mb-1">
                  This will apply changes immediately
                </div>
                <div className="text-amber-800 dark:text-amber-200">
                  All changes will be published to the live system. You can revert to a previous release if needed.
                </div>
                {pricingChangeCount > 0 && (
                  <div className="mt-2 text-amber-900 dark:text-amber-100 font-medium">
                    {pricingChangeCount} of these {pricingChangeCount === 1 ? 'is a pricing change' : 'are pricing changes'} and
                    {' '}take effect at checkout as soon as this is published.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Error message. Manifold reports every problem with a pricing
              changeset at once, one per line — keep the line breaks. */}
          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive rounded-lg text-destructive text-sm whitespace-pre-line">
              {error}
            </div>
          )}
          
          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={publishing}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              disabled={publishing || !releaseName.trim()}
            >
              {publishing ? 'Publishing...' : 'Publish Release'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

